import { useCallback, useEffect, useRef, useState } from 'react';
import { StorageService } from '@/services/storage';

export interface PersistedTemplateChoices {
  selectedToneId: string | null;
  fieldValues: Record<string, string>;
  singletonMode?: boolean;
  writeIns?: Record<string, string>;
}

async function loadLastTemplateChoices(
  topicId: string
): Promise<PersistedTemplateChoices | null> {
  try {
    const raw = await StorageService.getSharingTemplateChoices(topicId);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedTemplateChoices;
  } catch {
    return null;
  }
}

function saveLastTemplateChoices(
  topicId: string,
  choices: PersistedTemplateChoices
): void {
  StorageService.setSharingTemplateChoices(
    topicId,
    JSON.stringify(choices)
  ).catch(() => undefined);
}

export interface ShareFormChoices {
  /** The chosen tone, or null while a multi-tone file is unanswered. */
  selectedToneId: string | null;
  setSelectedToneId: React.Dispatch<React.SetStateAction<string | null>>;
  /**
   * The raw "Share as" choice. What actually sizes a leg is
   * `effectiveSingletonMode`, which narrows this when no intent has more than
   * one recipient.
   */
  singletonMode: boolean;
  setSingletonMode: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Every field's value, keyed by field id. Field ids are UUID80s unique across
   * a whole sharing form or templates file, so one flat record holds every
   * intent's answers and each message reads the ids it declares.
   */
  fieldValues: Record<string, string>;
  setFieldValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setFieldValue: (id: string, value: string) => void;
  /**
   * The words the sender last left in each select's write-in, keyed by field
   * id, so picking an option and then reopening the write-in brings them back.
   */
  writeIns: Record<string, string>;
  setWriteIn: (id: string, words: string) => void;
}

/**
 * What the sender has chosen about the message they are writing — the tone,
 * whether to text people individually, and what they have typed into each
 * field, write-ins they left included — and the one storage record that
 * carries them between visits.
 *
 * The three are persisted together because they are answered together: coming
 * back to a topic should restore the message as it was left, not a third of
 * it.
 */
export function useShareFormChoices(topicId: string): ShareFormChoices {
  const [selectedToneId, setSelectedToneId] = useState<string | null>(null);
  const [singletonMode, setSingletonMode] = useState<boolean>(true);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const [writeIns, setWriteIns] = useState<Record<string, string>>({});

  const setFieldValue = useCallback((id: string, value: string) => {
    setFieldValues(v => ({ ...v, [id]: value }));
  }, []);

  const setWriteIn = useCallback((id: string, words: string) => {
    setWriteIns(w => ({ ...w, [id]: words }));
  }, []);

  const hasLoadedTemplateChoices = useRef(false);

  useEffect(() => {
    loadLastTemplateChoices(topicId).then(choices => {
      if (choices) {
        if (choices.selectedToneId) setSelectedToneId(choices.selectedToneId);
        if (Object.keys(choices.fieldValues).length > 0)
          // MERGE (persisted as the base, live edits win) rather than replace:
          // this local-storage read is async, so a fast user can already have
          // typed into a field before it resolves — replacing would wipe those
          // edits. This bit the news reshare flow (#447), where the seed
          // run-start's persisted values clobbered the just-entered greeting.
          setFieldValues(prev => ({ ...choices.fieldValues, ...prev }));
        if (typeof choices.singletonMode === 'boolean')
          setSingletonMode(choices.singletonMode);
        if (choices.writeIns)
          setWriteIns(prev => ({ ...choices.writeIns, ...prev }));
      }
      hasLoadedTemplateChoices.current = true;
    });
  }, [topicId]);

  useEffect(() => {
    if (!hasLoadedTemplateChoices.current) return;
    saveLastTemplateChoices(topicId, {
      selectedToneId,
      fieldValues,
      singletonMode,
      writeIns,
    });
  }, [topicId, selectedToneId, fieldValues, singletonMode, writeIns]);

  return {
    selectedToneId,
    setSelectedToneId,
    singletonMode,
    setSingletonMode,
    fieldValues,
    setFieldValues,
    setFieldValue,
    writeIns,
    setWriteIn,
  };
}
